import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';

/**
 * "Look inside" · Volume 4: Circles.
 * Sección autocontenida (colores propios del tomo) para pegar en la landing.
 * Mobile: 1 slide al ~78% con las vecinas asomando, swipe nativo con scroll-snap.
 * Desktop: 3 visibles, la central a escala 1 y las laterales a 0.88 / 60%.
 * Sin autoplay; flechas y teclado hacen loop en los extremos.
 */

const DESKTOP_QUERY = '(min-width: 1024px)';
const EASE = 'ease-[cubic-bezier(0.23,1,0.32,1)]';
const FOCUS = 'focus:outline-none focus-visible:ring-4 focus-visible:ring-[#34406B]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#EEF1F4]';
export function VolumeCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [active, setActive] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const total = volumePages.length;
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /** scrollLeft en el que la slide i queda centrada. */
  const centerLeft = useCallback((i: number) => {
    const track = trackRef.current;
    const slide = track?.children[i] as HTMLElement | undefined;
    if (!track || !slide) return 0;
    return slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;
  }, []);
  const handleScroll = () => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < total; i++) {
        const dist = Math.abs(centerLeft(i) - track.scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      setActive(best);
    });
  };
  const goTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const target = (i % total + total) % total;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: centerLeft(target),
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    }
  };
  const arrowClass = `absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#34406B] text-white shadow-[0_8px_20px_rgba(52,64,107,0.25)] transition-[background-color,transform] duration-150 ${EASE} hover:bg-[#2A3459] active:scale-95 lg:flex ${FOCUS}`;
  return <section aria-labelledby="look-inside-heading" className="w-full overflow-hidden bg-[#FBF9F6] font-['Inter',system-ui,sans-serif] text-[#1E2433]" data-id="element-314" data-mp="8s161e865pgj">
      <div className="relative mx-auto max-w-3xl px-5 pt-16 text-center sm:pt-24" data-id="element-315" data-mp="iimdzs9qg6c4">
        <svg aria-hidden="true" viewBox="0 0 400 400" className="pointer-events-none absolute left-1/2 top-4 h-[360px] w-[360px] -translate-x-1/2 sm:h-[460px] sm:w-[460px]" fill="none" stroke="#34406B" strokeOpacity="0.06" strokeWidth="1" data-id="element-316" data-mp="ikqee08fan7n">
          {[40, 80, 120, 160, 196].map(r => <circle key={r} cx="200" cy="200" r={r} data-id="element-317" data-mp="3vzdaqe5ik1s1" />)}
        </svg>

        <p className="relative text-xs font-semibold uppercase tracking-[0.2em] text-[#34406B]" data-id="element-318" data-mp="167f4383objyt">
          Volume 4 of 6 · Circles
        </p>
        <h2 id="look-inside-heading" className="relative mt-4 font-['DM_Serif_Display',Georgia,serif] text-[36px] font-normal leading-[1.1] text-[#34406B] sm:text-[52px]" data-id="element-319" data-mp="1b9e6ofrlacup">
          See exactly what you get inside
        </h2>
        <p className="relative mx-auto mt-4 max-w-xl text-base leading-relaxed text-[#1E2433]/80 sm:text-lg" data-id="element-320" data-mp="1is402xu09x8d">
          Real pages from the pack. Every circle, card and worksheet is ready to
          print or run from your phone.
        </p>
      </div>

      <div className="mt-10 bg-[#EEF1F4] py-10 sm:mt-14 sm:py-14" data-id="element-321" data-mp="1nq5dp38nx4cg">
        <div className="relative mx-auto max-w-5xl lg:px-20" role="region" aria-roledescription="carousel" aria-label="Volume 4 sample pages" onKeyDown={handleKeyDown} data-id="element-322" data-mp="1npx1zeoa052u">
          <button type="button" onClick={() => goTo(active - 1)} aria-label="Previous page" className={`${arrowClass} left-2`} data-id="element-323" data-mp="2bfrdnswuznli">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-324" data-mp="26etvvhfrgvkj" />
          </button>

          <div ref={trackRef} onScroll={handleScroll} className="relative flex snap-x snap-mandatory items-center gap-3 overflow-x-auto px-[11%] py-4 lg:gap-0 lg:px-[33.333%] [&::-webkit-scrollbar]:hidden" style={{
          scrollbarWidth: 'none'
        }} data-id="element-325" data-mp="2q0c8hale0xqk">
            {volumePages.map((page, i) => {
            const isActive = i === active;
            const sideStyle = isDesktop && !isActive ? 'scale-[0.88] opacity-60' : 'scale-100 opacity-100';
            return <div key={page.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${total}`}
            // El track tiene padding lateral (11% mobile / 33.3% desktop), así que
            // w-full = 78% del ancho en mobile y un tercio en desktop.
            className="w-full flex-none snap-center lg:px-2" data-id="element-326" data-mp="1xdrfy6t6eflf" data-mpi={i}>
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full screen: ${page.alt}`} className={`block w-full overflow-hidden rounded-2xl border border-[#E6E1DA] bg-white shadow-[0_12px_32px_rgba(52,64,107,0.15)] transition-[transform,opacity] duration-300 ${EASE} ${sideStyle} ${FOCUS}`} data-id="element-327" data-mp="19auyg0woxy6c">
                    <img src={page.src} alt={page.alt} width={1080} height={1920} loading={i < 2 ? 'eager' : 'lazy'} decoding="async" className="aspect-[9/16] w-full bg-white object-contain" data-id="element-328" data-mp="1s85uzdqxudi3" />
                  </button>
                </div>;
          })}
          </div>

          <button type="button" onClick={() => goTo(active + 1)} aria-label="Next page" className={`${arrowClass} right-2`} data-id="element-329" data-mp="1w8abp9fz96z">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-330" data-mp="2iieonuvzuxu3" />
          </button>
        </div>

        <div className="mx-auto mt-6 max-w-xl px-5 text-center" data-id="element-331" data-mp="2nhiwoi76mpiu">
          <p className="min-h-[3rem] text-[15px] font-medium leading-snug text-[#1E2433] sm:text-base" aria-live="polite" data-id="element-332" data-mp="2ng7ugmtdxptu">
            {volumePages[active].caption}
          </p>
          <p className="mt-2 text-xs font-semibold tabular-nums tracking-wider text-[#34406B]/70" data-id="element-333" data-mp="2x7kmpfj3ii0e">
            {active + 1} / {total}
          </p>

          <div className="mt-4 flex items-center justify-center" data-id="element-334" data-mp="3680omkk6xh35">
            {volumePages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === active ? 'true' : undefined} className={`flex h-7 w-6 items-center justify-center rounded-full ${FOCUS}`} data-id="element-335" data-mp="3b7vfio3yu6o7" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ${EASE} ${i === active ? 'w-5 bg-[#34406B]' : 'w-2 bg-[#F2C6A8]'}`} data-id="element-336" data-mp="3ip1msyy1ols6" />
              </button>)}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md px-5 pb-16 pt-10 sm:pb-24" data-id="element-337" data-mp="2zs0fh7h0q9l2">
        <a href="#checkout" className="flex min-h-[56px] w-full items-center justify-center rounded-xl bg-[#D9774B] px-6 py-4 text-center text-lg font-bold text-white shadow-[0_10px_24px_-10px_rgba(217,119,75,0.7)] transition-[background-color,transform] duration-150 ease-out hover:bg-[#C76A40] active:scale-[0.98] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#D9774B]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBF9F6]" data-id="element-338" data-mp="2zucwnieycfx2">
          Get all 6 volumes
        </a>
      </div>

      <AnimatePresence data-id="element-339" data-mp="rkx1jlqn0hqc">
        {lightboxIndex !== null && <PageLightbox pages={volumePages} startIndex={lightboxIndex} label="Volume 4 pages" onClose={() => setLightboxIndex(null)} data-id="element-340" data-mp="rmq8z392r5ze" />}
      </AnimatePresence>
    </section>;
}